/**
 * Pipeline-analyse: onderste sectie van het schoorsteenveger-dashboard.
 * Standaard dichtgeklapt (zelfde progressive-disclosure-patroon als de
 * Funnel Diagnose op het admin-dashboard); data laadt pas bij openklappen.
 *
 * 1. Aanvragen per dag (hele historie — de seizoenswave)
 * 2. Per maand + conversie (uitgevoerd vs niet-uitgevoerd + conversie%-lijn)
 * 3. De trechter: huidige verdeling over de pipeline-stages + On Hold-inzicht
 */

import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, LineChart, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { getPipelineAnalytics } from '../../utils/schoorsteenvegerQueries';
import InfoDot from './InfoDot';

const num = (v) => new Intl.NumberFormat('nl-NL').format(v || 0);

const MONTH_SHORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_SHORT[m - 1]} '${String(y).slice(2)}`;
};
const dayTick = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return d === 1 ? `${MONTH_SHORT[m - 1]} '${String(y).slice(2)}` : '';
};

// Stage-kleur: groen = uitgevoerd/gefactureerd (5-8), rood = uit planning (4),
// grijs = onderweg (1-3)
const stageColor = (order) => {
  if (order === 4) return '#dc2626';
  if (order >= 5) return '#059669';
  return 'var(--color-neutral-400)';
};

const tooltipStyle = {
  background: 'var(--tooltip-bg)',
  border: '1px solid var(--tooltip-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: '12px',
  color: 'var(--color-neutral-800)',
};

const PipelineAnalytics = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true);
    getPipelineAnalytics().then((d) => { setData(d); setLoading(false); });
  }, [open, data, loading]);

  const onHold = data?.stages.find((s) => s.order === 4);
  const onHoldPct = onHold && data?.total ? Math.round((onHold.count / data.total) * 100) : null;
  const maxStage = data ? Math.max(...data.stages.map((s) => s.count), 1) : 1;

  return (
    <div style={{ marginTop: 'var(--space-6)' }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px',
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em',
          color: 'var(--color-neutral-600)',
        }}
      >
        <span>{open ? '▼' : '▶'}</span>
        <span>PIPELINE-ANALYSE</span>
        <span style={{ fontWeight: 400, letterSpacing: 0, color: 'var(--color-neutral-400)', textTransform: 'none' }}>
          aanvragen per dag · maandconversie · trechter (hele historie)
        </span>
      </button>

      {open && (
        <div>
          {loading && (
            <div className="card-blue"><div className="card-body">
              <div style={{ height: '220px', background: 'var(--color-neutral-100)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
            </div></div>
          )}

          {data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }} className="lg:grid-cols-2">
                {/* 1. Aanvragen per dag */}
                <div className="card-blue">
                  <div className="card-header">
                    <h3 className="card-title">Aanvragen per dag</h3>
                    <InfoDot text={`Aantal nieuwe aanvragen per dag, hele historie. De seizoenswave: rustig in voorjaar/zomer, sterke piek in september-oktober (pre-stookseizoen), daarna afbouw.${data.importFiltered > 0 ? ` Importdag 12 mrt '25 (${num(data.importFiltered)} in bulk geladen) is uitgefilterd.` : ''}`} />
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data.daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={dayTick} interval={0} tickLine={false}
                          tick={{ fontSize: 10, fill: 'var(--color-neutral-500)' }} axisLine={{ stroke: 'var(--color-neutral-200)' }} />
                        <YAxis tick={{ fontSize: 10, fill: 'var(--color-neutral-500)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={tooltipStyle}
                          labelFormatter={(d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                          formatter={(v) => [num(v), 'aanvragen']} />
                        <Line type="monotone" dataKey="count" stroke="var(--color-blue-500)" strokeWidth={1.4} dot={false} name="aanvragen" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 2. Per maand + conversie */}
                <div className="card-blue">
                  <div className="card-header">
                    <h3 className="card-title">Per maand + conversie</h3>
                    <InfoDot text="Elke balk is één maand aanvragen. Fel blauw = daadwerkelijk uitgevoerd/gefactureerd, gedempt = (nog) niet uitgevoerd. De groene lijn (rechter-as) is datzelfde als conversiepercentage. De laatste maand is nog vers en dus laag." />
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart data={data.monthly} margin={{ top: 5, right: -14, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-neutral-200)" vertical={false} />
                        <XAxis dataKey="month" tickFormatter={monthLabel} tickLine={false}
                          tick={{ fontSize: 10, fill: 'var(--color-neutral-500)' }} axisLine={{ stroke: 'var(--color-neutral-200)' }} />
                        <YAxis yAxisId="n" tick={{ fontSize: 10, fill: 'var(--color-neutral-500)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <YAxis yAxisId="pct" orientation="right" domain={[0, 100]} tickFormatter={(v) => `${v}%`}
                          tick={{ fontSize: 10, fill: 'var(--color-neutral-500)' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={tooltipStyle} labelFormatter={monthLabel}
                          formatter={(v, n) => (n === 'conversie %' ? [`${v}%`, n] : [num(v), n])} />
                        <Legend wrapperStyle={{ fontSize: '11px' }} />
                        <Bar yAxisId="n" dataKey="uitgevoerd" stackId="a" fill="var(--color-blue-500)" name="uitgevoerd" />
                        <Bar yAxisId="n" dataKey="nietUitgevoerd" stackId="a" fill="var(--color-neutral-300)" name="niet uitgevoerd" radius={[3, 3, 0, 0]} />
                        <Line yAxisId="pct" type="monotone" dataKey="conversie" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} name="conversie %" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* 3. De trechter: waar staan ze nu */}
              <div className="card-blue" style={{ marginBottom: 'var(--space-6)' }}>
                <div className="card-header">
                  <h3 className="card-title">De trechter (waar staan ze nu)</h3>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '0.72rem', color: 'var(--color-neutral-500)' }}>
                    {num(data.total)} aanvragen totaal
                    <InfoDot text="Huidige verdeling van álle aanvragen over de pipeline-stages. Groen = uitgevoerd/gefactureerd (conversie), grijs = onderweg, rood = uit planning gehaald." />
                  </span>
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                  {data.stages.map((s) => (
                    <div key={s.order} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ width: '150px', flexShrink: 0, fontSize: '0.78rem', color: 'var(--color-neutral-600)', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.order}. {s.name.replace(/\s*\(.*\)$/, '')}
                      </span>
                      <div style={{ flex: 1, height: '16px', background: 'var(--color-neutral-100)', borderRadius: '8px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.max((s.count / maxStage) * 100, s.count > 0 ? 1.5 : 0)}%`, background: stageColor(s.order), borderRadius: '8px', transition: 'width .4s ease' }} />
                      </div>
                      <span style={{ width: '52px', flexShrink: 0, fontSize: '0.8rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                        {num(s.count)}
                      </span>
                    </div>
                  ))}
                  {onHold && onHoldPct != null && onHoldPct >= 10 && (
                    <div style={{ marginTop: '6px', padding: '10px 14px', borderLeft: '3px solid #dc2626', background: 'rgba(220, 38, 38, 0.06)', borderRadius: '0 var(--radius-md) var(--radius-md) 0', fontSize: '0.8rem', color: 'var(--color-neutral-700)' }}>
                      <strong>Opvallend:</strong> {num(onHold.count)} aanvragen ({onHoldPct}%) staan op "On Hold (Uit Planning)" — de grootste bron van verloren conversie.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelineAnalytics;
